'use client';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { MapPinIcon, XIcon } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import { Label } from '@workspace/ui/components/label';
import { Slider } from '@workspace/ui/components/slider';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';

export interface FilterState {
  region?: string;
  radius: number;
  minBudget?: number;
  maxBudget?: number;
  minGuestCount?: number;
  maxGuestCount?: number;
}

interface ExplorerFiltersProps {
  regions: { region: string; location: string; count: number }[];
  onFilterChange: (filters: FilterState) => void;
  isLoading?: boolean;
}

const DEFAULT_RADIUS = 100;
const MAX_BUDGET_LIMIT = 250000;

export function ExplorerFilters({
  regions,
  onFilterChange,
  isLoading = false,
}: ExplorerFiltersProps): React.JSX.Element {
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>(undefined);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [minBudget, setMinBudget] = useState<number | undefined>(undefined);
  const [maxBudget, setMaxBudget] = useState<number | undefined>(undefined);
  const [minGuestCount, setMinGuestCount] = useState<number | undefined>(undefined);
  const [maxGuestCount, setMaxGuestCount] = useState<number | undefined>(undefined);

  // Notify parent of filter changes
  useEffect(() => {
    onFilterChange({
      region: selectedRegion,
      radius,
      minBudget,
      maxBudget,
      minGuestCount,
      maxGuestCount,
    });
  }, [selectedRegion, radius, minBudget, maxBudget, minGuestCount, maxGuestCount, onFilterChange]);

  const resetFilters = useCallback(() => {
    setSelectedRegion(undefined);
    setRadius(DEFAULT_RADIUS);
    setMinBudget(undefined);
    setMaxBudget(undefined);
    setMinGuestCount(undefined);
    setMaxGuestCount(undefined);
  }, []);

  const hasActiveFilters =
    selectedRegion !== undefined ||
    minBudget !== undefined ||
    maxBudget !== undefined ||
    minGuestCount !== undefined ||
    maxGuestCount !== undefined;

  return (
    <div className="space-y-6">
      {/* Region Selection */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <MapPinIcon className="h-4 w-4" />
          Region
        </Label>
        <Select
          value={selectedRegion || 'all'}
          onValueChange={(value) => setSelectedRegion(value === 'all' ? undefined : value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="All regions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            {regions.map((region) => (
              <SelectItem key={region.region} value={region.region}>
                {region.location} ({region.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Quick Region Buttons */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Popular Regions</Label>
        <div className="flex flex-wrap gap-1.5">
          {regions.slice(0, 6).map((region) => (
            <Button
              key={region.region}
              variant={selectedRegion === region.region ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedRegion(
                selectedRegion === region.region ? undefined : region.region
              )}
            >
              {region.location.split(',')[0]}
            </Button>
          ))}
        </div>
      </div>

      {/* Budget Range */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Budget Range</Label>
          {(minBudget !== undefined || maxBudget !== undefined) && (
            <span className="text-sm text-muted-foreground">
              {minBudget !== undefined ? `$${(minBudget / 1000).toFixed(0)}k` : '$0'}
              {' - '}
              {maxBudget !== undefined ? `$${(maxBudget / 1000).toFixed(0)}k` : '$250k+'}
            </span>
          )}
        </div>
        
        {/* Minimum Budget Slider */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Minimum</span>
            <span className="text-xs font-mono">
              {minBudget !== undefined ? `$${minBudget.toLocaleString()}` : 'No min'}
            </span>
          </div>
          <Slider
            value={[minBudget ?? 0]}
            onValueChange={([value]) =>
              setMinBudget(value === 0 ? undefined : value)
            }
            min={0}
            max={MAX_BUDGET_LIMIT - 5000}
            step={5000}
            className="w-full"
          />
        </div>
        
        {/* Maximum Budget Slider */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Maximum</span>
            <span className="text-xs font-mono">
              {maxBudget !== undefined ? `$${maxBudget.toLocaleString()}` : 'No max'}
            </span>
          </div>
          <Slider
            value={[maxBudget ?? MAX_BUDGET_LIMIT]}
            onValueChange={([value]) =>
              setMaxBudget(value === MAX_BUDGET_LIMIT ? undefined : value)
            }
            min={5000}
            max={MAX_BUDGET_LIMIT}
            step={5000}
            className="w-full"
          />
        </div>
        
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>$0</span>
          <span>$250k+</span>
        </div>
      </div>

      {/* Guest Count Range */}
      <div className="space-y-3">
        <Label>Guest Count Range</Label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Input
              type="number"
              placeholder="Min"
              value={minGuestCount ?? ''}
              onChange={(e) =>
                setMinGuestCount(e.target.value ? parseInt(e.target.value) : undefined)
              }
              className="h-8 text-sm"
              min={0}
              max={500}
            />
          </div>
          <div>
            <Input
              type="number"
              placeholder="Max"
              value={maxGuestCount ?? ''}
              onChange={(e) =>
                setMaxGuestCount(e.target.value ? parseInt(e.target.value) : undefined)
              }
              className="h-8 text-sm"
              min={0}
              max={500}
            />
          </div>
        </div>
      </div>

      {/* Reset Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={resetFilters}
          disabled={isLoading}
        >
          <XIcon className="h-4 w-4 mr-2" />
          Reset Filters
        </Button>
      )}
    </div>
  );
}

